import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { MapPin, Plus, Pencil, Trash2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Sectores() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [sectores, setSectores] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ area_id: '', nombre: '' });
  const [openAreas, setOpenAreas] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [secRes, areasRes] = await Promise.all([
        axios.get(`${API}/sectores`, { headers }),
        axios.get(`${API}/areas`, { headers })
      ]);
      setSectores(secRes.data);
      setAreas(areasRes.data);
    } catch { }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = sectores.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.nombre.toLowerCase().includes(q) || s.area_nombre?.toLowerCase().includes(q);
    const matchArea = !filtroArea || s.area_id === filtroArea;
    return matchSearch && matchArea;
  });

  const openNew = () => {
    setEditing(null);
    setForm({ area_id: areas[0]?.id || '', nombre: '' });
    setShowModal(true);
  };

  const openEdit = (sector) => {
    setEditing(sector);
    setForm({ area_id: sector.area_id, nombre: sector.nombre });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.area_id) { toast.error('Complete todos los campos'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/sectores/${editing.id}`, form, { headers });
        toast.success('Sector actualizado');
      } else {
        await axios.post(`${API}/sectores`, form, { headers });
        toast.success('Sector creado');
      }
      setShowModal(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    setConfirmDelete({ onConfirm: async () => {
      setConfirmDelete(null);
      try {
        await axios.delete(`${API}/sectores/${id}`, { headers });
        toast.success('Sector eliminado');
        fetchData();
      } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    }});
  };

  // Group by area
  const grouped = {};
  filtered.forEach(s => {
    const key = s.area_nombre || 'Sin Area';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });

  return (
    <div className="space-y-6" data-testid="sectores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sectores por Area</h1>
          <p className="text-sm text-slate-500 mt-0.5">Administre los sectores asociados a cada area</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors"
          data-testid="nuevo-sector-btn">
          <Plus className="w-4 h-4" />Nuevo Sector
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar sectores..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
              data-testid="search-sectores" />
          </div>
          <select value={filtroArea} onChange={e => setFiltroArea(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-area">
            <option value="">Todas las areas</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <MapPin className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">No hay sectores registrados</p>
            <p className="text-xs text-slate-400 mt-1">Cree un nuevo sector para comenzar</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([areaName, secs]) => {
              const isOpen = openAreas[areaName] === true;
              return (
              <div key={areaName}>
                <button onClick={() => setOpenAreas(prev => ({ ...prev, [areaName]: !isOpen }))}
                  className="w-full bg-slate-50 px-4 py-2.5 flex items-center gap-2 hover:bg-slate-100 transition-colors">
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                  <MapPin className="w-4 h-4 text-[#a05e49]" />
                  <span className="text-sm font-semibold text-slate-700">{areaName}</span>
                  <span className="text-xs text-slate-400 ml-1">({secs.length})</span>
                </button>
                {isOpen && (
                <div className="divide-y divide-slate-100">
                  {secs.map(s => (
                    <div key={s.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50" data-testid={`sector-${s.id}`}>
                      <span className="text-sm text-slate-700">{s.nombre}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(s)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" data-testid={`edit-${s.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(s.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">{editing ? 'Editar Sector' : 'Nuevo Sector'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Area *</label>
                <select value={form.area_id} onChange={e => setForm(f => ({ ...f, area_id: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" data-testid="modal-select-area">
                  <option value="">Seleccionar area</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Nombre del Sector *</label>
                <input type="text" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Banco 3860"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
                  data-testid="modal-input-nombre" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="modal-save-btn">
                {editing ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      <ConfirmModal open={!!confirmDelete} title="Eliminar sector" message="Esta seguro de eliminar este sector?"
        confirmLabel="Eliminar" variant="danger" onConfirm={confirmDelete?.onConfirm} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}
